/**
 * Utility functions for image processing in DIYBuddy
 */

/**
 * Resizes an image to a maximum dimension while maintaining aspect ratio
 * @param file - The original image file
 * @param maxSize - Maximum width or height in pixels (default: 2048)
 * @param quality - JPEG compression quality (0-1, default: 0.9 for light compression)
 * @returns A promise that resolves to the resized File object in JPEG format
 */
export const resizeImage = (file: File, maxSize: number = 2048, quality: number = 0.9): Promise<File> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (readerEvent) => {
      const image = new Image();
      
      image.onload = () => {
        // Determine if resizing is needed
        let width = image.width;
        let height = image.height;
        
        // Only resize if either dimension exceeds maxSize
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            // Landscape image
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            // Portrait image
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
          
          // Create canvas for resizing
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          // Draw resized image on canvas
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(image, 0, 0, width, height);
          
          // Convert canvas to Blob as JPEG with specified quality
          canvas.toBlob((blob) => {
            if (blob) {
              // Create new File from Blob with JPEG type
              const resizedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              
              // Clean up to prevent memory leaks
              URL.revokeObjectURL(image.src);
              resolve(resizedFile);
            } else {
              // If conversion fails, convert original to JPEG
              const canvas = document.createElement('canvas');
              canvas.width = image.width;
              canvas.height = image.height;
              const ctx = canvas.getContext('2d');
              ctx?.drawImage(image, 0, 0);
              canvas.toBlob((blob) => {
                if (blob) {
                  const jpegFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  });
                  
                  // Clean up to prevent memory leaks
                  URL.revokeObjectURL(image.src);
                  resolve(jpegFile);
                } else {
                  // Last resort fallback
                  URL.revokeObjectURL(image.src);
                  resolve(file);
                }
              }, "image/jpeg", quality);
            }
          }, "image/jpeg", quality); // Explicitly set JPEG format with quality
        } else {
          // No resizing needed, but still convert to JPEG
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(image, 0, 0);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const jpegFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              
              // Clean up to prevent memory leaks
              URL.revokeObjectURL(image.src);
              resolve(jpegFile);
            } else {
              // Fallback to original
              URL.revokeObjectURL(image.src);
              resolve(file);
            }
          }, "image/jpeg", quality);
        }
      };
      
      // Handle errors on image load
      image.onerror = () => {
        console.error("Failed to load image");
        resolve(file); // Fallback to original file
      };
      
      // Set image source with proper error handling
      if (readerEvent.target?.result) {
        image.src = readerEvent.target.result as string;
      } else {
        resolve(file); // Fallback if no result
      }
    };
    
    // Handle errors on file read
    reader.onerror = () => {
      console.error("Failed to read file");
      resolve(file); // Fallback to original file
    };
    
    // Read the file as Data URL
    reader.readAsDataURL(file);
  });
};
